import { type ClassValue, clsx } from 'clsx';
import { format, isValid, type Locale, parseISO } from 'date-fns';
import enUS from 'date-fns/locale/en-US/index.js';
import es from 'date-fns/locale/es/index.js';
import ptBR from 'date-fns/locale/pt-BR/index.js';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Conversões de horário de parede São Paulo (UTC-3 fixo).
// Brasil aboliu o horário de verão em 2019 — UTC-3 fixo é correto e estável.
// Convenção espelha o backend (generate-sessions-from-recurrence.ts):
//   instante UTC = parede São Paulo + 3h.  Ex.: 09:00 SP -> 12:00Z.
export const SAO_PAULO_TZ = 'America/Sao_Paulo';

// "YYYY-MM-DD" + "HH:MM" (parede SP) -> instante UTC. Ex.: 09:00 SP -> 12:00Z.
export const wallClockToUtc = (date: string, time: string): Date => zonedTimeToUtc(`${date}T${time}:00`, SAO_PAULO_TZ);

// Instante UTC -> "YYYY-MM-DD" (parede SP). 12:00Z -> "2026-06-21".
export const utcToWallClockDate = (d: Date): string => formatInTimeZone(d, SAO_PAULO_TZ, 'yyyy-MM-dd');

// Instante UTC -> "HH:MM" (parede SP). 12:00Z -> "09:00".
export const utcToWallClockTime = (d: Date): string => formatInTimeZone(d, SAO_PAULO_TZ, 'HH:mm');

// Fim do dia corrente (23:59, parede SP) -> instante UTC. Upper bound de filtros
// "até hoje" que não deve travar no horário da consulta.
export const endOfTodayUtc = (): Date => wallClockToUtc(utcToWallClockDate(new Date()), '23:59');

// String vai por `parseISO`; número e Date por `new Date`. A distinção importa para data de
// calendário: `new Date('2026-07-25')` é lido como meia-noite UTC e recua um dia em UTC-3,
// enquanto `parseISO` devolve meia-noite local. Para instante ISO completo os dois são
// equivalentes, então essa é a forma correta para os dois tipos de entrada.
//
// Não confundir com os helpers `wallClock` acima: aqueles convertem um instante para a parede
// de São Paulo. Aqui não há conversão de fuso — só leitura correta do que foi recebido.
export const formatDate = ({ date, dateFormat }: { date?: string | number | Date | null; dateFormat: string }) => {
  if (!date) return undefined;
  const parsed = typeof date === 'string' ? parseISO(date) : new Date(date);
  // `format` do date-fns lança RangeError em data inválida, e os call sites tratam
  // apenas `undefined`.
  if (!isValid(parsed)) return undefined;
  return format(parsed, dateFormat);
};

const LOCALES_BY_LANG: Record<string, Locale> = {
  'pt-br': ptBR,
  'en-us': enUS,
  'es-es': es,
};

const toDateFnsLocale = (lang?: string): Locale => LOCALES_BY_LANG[lang?.toLowerCase() ?? ''] ?? ptBR;

// Formata um instante UTC na parede de São Paulo com locale e `style` (padrão date-fns) livres.
// Usado por PDFs/mensagens que precisam de i18n na formatação (lang cai pra pt-BR se desconhecido).
export const formatDateInTimeZone = ({ date, style, lang }: { date: Date; style: string; lang?: string }): string =>
  formatInTimeZone(date, SAO_PAULO_TZ, style, { locale: toDateFnsLocale(lang) });

export const formatNumber = (value: number, options?: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'decimal',
    notation: 'standard',
    maximumFractionDigits: 2,
    ...options,
  }).format(value);

export const formatMoney = (value: number, compact = false) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: compact ? 'compact' : 'standard',
    maximumFractionDigits: 2,
  }).format(value);

// Faixa Unicode das marcas diacríticas combinantes (produzidas por normalize('NFD')) — evita \p{Diacritic},
// que exige a flag /u e quebra o typecheck de apps com target ES5 (participant).
export const normalizeForSearch = (value: string | null | undefined) =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
